import { Injectable } from '@angular/core';
import { HttpClientService } from '../http-client/http-client.service';
import { LocalStorageService } from '../localStorage/local-storage.service';
import { OpenAppsRegistryService } from './open-apps-registry.service';

/** Intervalo usado até a API informar `pingSegundos` na primeira resposta. */
const PING_PADRAO_SEGUNDOS = 30;

/** Lock que só a aba líder detém: quem o tem é o único que envia o ping. */
const NOME_LOCK_PING = 'vivest-center:session-ping';

/** Ações do usuário que contam como atividade para o painel de sessões. */
const EVENTOS_ATIVIDADE = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

const OPCOES_ATIVIDADE: AddEventListenerOptions = { capture: true, passive: true };

/**
 * Envia o ping de sessão do VivestCenter (`POST v1/auth/heartbeat`) para o painel
 * de sessões do administrador saber quem está online, onde está e se houve
 * atividade recente.
 *
 * - Só a aba líder envia: a que detém o lock `vivest-center:session-ping`. Se ela
 *   fechar, outra aba aberta assume.
 * - Só sessões do VivestCenter (login Portal ou AD). IntegWeb não pinga.
 * - Para no logout, quando o bearerToken some.
 * - Falha de ping é ignorada: o 401 já passa pelo authInterceptor, e o próximo
 *   ciclo tenta de novo.
 */
@Injectable({
  providedIn: 'root',
})
export class SessionPingService {
  private rodando = false;
  /** Incrementado a cada parada: um ciclo antigo que termina depois não reagenda. */
  private geracao = 0;
  private controle?: AbortController;
  private liberarLideranca?: () => void;
  private timer?: ReturnType<typeof setTimeout>;
  private intervaloSegundos = PING_PADRAO_SEGUNDOS;
  private atividadeDesdeUltimoPing = false;

  /**
   * Marcar que houve atividade é só ligar um booleano: não precisa de throttle.
   * A ação do usuário só afeta o próximo ping.
   */
  private readonly marcarAtividade = (): void => {
    this.atividadeDesdeUltimoPing = true;
  };

  constructor(
    private httpClientService: HttpClientService,
    private localStorageService: LocalStorageService,
    private openAppsRegistry: OpenAppsRegistryService,
  ) {}

  /**
   * Liga o ping se a sessão atual é do VivestCenter e desliga caso contrário.
   * Pode ser chamado a cada navegação: não faz nada se o estado já está certo.
   */
  sincronizar(): void {
    if (this.sessaoVivestCenter()) {
      this.iniciar();
    } else {
      this.parar();
    }
  }

  private iniciar(): void {
    if (this.rodando) {
      return;
    }

    this.rodando = true;
    const geracao = ++this.geracao;

    // Sem Web Locks (contexto não seguro), não há como coordenar as abas: cada uma pinga.
    if (!navigator.locks) {
      this.virarLider(geracao);
      return;
    }

    const controle = new AbortController();
    this.controle = controle;

    // O lock fica preso enquanto a callback não resolver: a aba mantém a liderança
    // até parar (logout) ou fechar.
    navigator.locks
      .request(NOME_LOCK_PING, { signal: controle.signal }, () =>
        new Promise<void>((liberar) => {
          if (geracao !== this.geracao) {
            liberar();
            return;
          }

          this.liberarLideranca = liberar;
          this.virarLider(geracao);
        }),
      )
      // Abortado antes de virar líder (logout com o lock ainda na fila): nada a fazer.
      .catch(() => {});
  }

  private virarLider(geracao: number): void {
    EVENTOS_ATIVIDADE.forEach((evento) =>
      document.addEventListener(evento, this.marcarAtividade, OPCOES_ATIVIDADE),
    );

    void this.pingar(geracao);
  }

  private async pingar(geracao: number): Promise<void> {
    if (geracao !== this.geracao) {
      return;
    }

    if (!this.sessaoVivestCenter()) {
      this.parar();
      return;
    }

    // Foco dentro de um iframe de microfrontend também é atividade: o clique
    // que entrou nele não chega ao documento do chassi.
    const ativo = this.atividadeDesdeUltimoPing || document.activeElement?.tagName === 'IFRAME';
    this.atividadeDesdeUltimoPing = false;

    try {
      const resposta = await this.httpClientService.heartbeat({
        ativo,
        menus: this.openAppsRegistry.listar(),
      });

      const segundos = Number(resposta.body?.pingSegundos);

      if (segundos > 0) {
        this.intervaloSegundos = segundos;
      }
    } catch {
      // Ignorado de propósito: a próxima tentativa acontece no próximo ciclo.
    }

    if (geracao === this.geracao) {
      this.timer = setTimeout(() => void this.pingar(geracao), this.intervaloSegundos * 1000);
    }
  }

  private parar(): void {
    if (!this.rodando) {
      return;
    }

    this.rodando = false;
    this.geracao++;

    this.controle?.abort();
    this.controle = undefined;

    // Resolver a callback do lock libera a liderança para a próxima aba.
    this.liberarLideranca?.();
    this.liberarLideranca = undefined;

    clearTimeout(this.timer);
    this.timer = undefined;

    EVENTOS_ATIVIDADE.forEach((evento) =>
      document.removeEventListener(evento, this.marcarAtividade, OPCOES_ATIVIDADE),
    );
    this.atividadeDesdeUltimoPing = false;
  }

  /** Sessão do VivestCenter: login Portal (CPF) ou AD, com bearerToken presente. */
  private sessaoVivestCenter(): boolean {
    const tipo = this.localStorageService.get('AuthType');

    return (tipo === 'Portal' || tipo === 'AD') && !!this.localStorageService.get('bearerToken');
  }
}
